import React from 'react';
import { when } from 'mobx';
import { types, getEnv, flow, addDisposer } from 'mobx-state-tree';
import _get from 'lodash.get';
import { FormattedMessage } from 'react-intl';

import { DOCUMENTS, fetchSavedDocumentDetails, NEW_DOCUMENT_QUERY_PARAM_KEY, noticeError } from '@ratehub/base-ui';
import { getCreditCardURL, CC_URL_SLUGS } from '@ratehub/cc-common';

import Pages from '../definitions/Pages';
import { TabValues, TabMessages } from '../definitions/TabValues';
import SavedDocumentsRowAuto from '../components/SavedDocumentsRowAuto';
import SavedDocumentsRowHome from '../components/SavedDocumentsRowHome';
import extractAutoHomeRows from '../functions/extractAutoHomeRows';
import MortgageApplicationRow from '../components/MortgageApplicationRow';
import extractMortgageRows from '../functions/extractMortgageRows';
import getMortgagesNewDocumentHref from '../functions/getMortgagesNewDocumentHref';
import extractCreditCardRows from '../functions/extractCreditCardRows';
import CreditCardApplicationRow from '../components/CreditCardApplicationRow';


const SavedDocuments = types
    .model('Saved Documents', {
        id: types.optional(types.identifier, Pages.myQuotes.id),
        selectedTabId: types.maybeNull(types.string),
        isPullingDocuments: false,
    })
    .volatile(() => ({
        // _documents example
        // { insurance: { home : {
        //      'document_id_1' : { id: 'document_id_1', ... },
        //      'document_id_2' : { id: 'document_id_2', ... },
        // }}}
        _documents: null,
    }))
    .views(self => ({
        get context() {
            return getEnv(self);
        },
        get tabs() {
            return getTabList({ locale: self.context.viewStore.locale });
        },
        get userName() {
            return self.context.firstName;
        },
        get shouldShowSpinner() {
            return self.isPullingDocuments;
        },
        get isActive() {
            return self.context.viewStore.selectedPageModel === self;
        },
        get defaultTabIndex() {
            const found = self.tabs.findIndex(tab => tab.isDefaultTab);
            return found >= 0 ? found : 0;
        },
        get selectedTabIndex() {
            const found = self.tabs.findIndex(tab => tab.id === self.selectedTabId);
            return found >= 0 ? found : self.defaultTabIndex;
        },
        get activeRows() {
            const activeTab = self.tabs[self.selectedTabIndex];
            const tabDocuments = _get(self._documents, activeTab.documentPath);
            return tabDocuments ? activeTab.extractRows(Object.values(tabDocuments)) : [];
        },
        findTab(id) {
            return self.tabs.find(tab => tab.id === id);
        },
    }))
    .actions(self => ({
        afterCreate() {
            addDisposer(
                self,
                when(() => self.isActive, self._fetchDocuments, {
                    name: 'Accounts.ViewStore.SavedDocuments.FetchDocumentsWhenPageActive',
                }),
            );
        },
        removeRow(tabIndex, row) {
            const tab = self.tabs[tabIndex];

            // follow the path down to the document id and delete
            // and update self._documents with new object
            try {
                const updatedDocuments = { ...self._documents };
                let target = updatedDocuments;

                // Find the node we are going to delete
                tab.documentPath.split('.').forEach(subPath => {
                    target = target[subPath];
                });

                delete target[row.document.id];

                self._documents = updatedDocuments;
            } catch (error) {
                noticeError(error, {
                    message: '[SavedDocuments.removeRow] error in deleting doc in self._documents',
                });
            }
        },
        selectTab(id) {
            self.selectedTabId = id;
        },
        _fetchDocuments: flow(function* () {
            self.isPullingDocuments = true;

            try {
                const documentIdentifiers = self.tabs.flatMap(tab => {
                    // 1) grab document metadata to know what documents user is keeping
                    return (
                        self.context.sessionStore
                            .getDocumentMetadataByPath(tab.documentPath)
                            // 2) filter out the metadata (ex. agreedPrivacyPolicy) if needed
                            .filter(metadata => (tab.metadataFilterFn ? tab.metadataFilterFn(metadata) : true))
                            // 3) make identifiers out of them
                            .map(metadata => {
                                const schemaVersion = tab.schemaVersion;
                                return schemaVersion
                                    ? { id: metadata.id, documentType: tab.documentType, schemaVersion }
                                    : { id: metadata.id, documentType: tab.documentType };
                            })
                    );
                });

                if (documentIdentifiers.length > 0) {
                    const { documents } = yield fetchSavedDocumentDetails(documentIdentifiers);
                    self._documents = documents;
                }
            } catch {
                // Error is noticed within fetchSavedDocumentDetails -> fetchJSON
                self.isPullingDocuments = false;
            }

            self.isPullingDocuments = false;
        }),
    }));

/**
 * This function generates an array
 * @param {Object} options To support flag-based values or condition-based values
 * @returns {Array} the array contains data to construct Table/Tab/Row components
 * Please note that extractRows() should return at least { key, document }
 */
function getTabList({ locale }) {
    return [
        // Mortgages
        {
            id: TabValues.MORTGAGE,

            // props in Tab
            tabTitle: TabMessages.MORTGAGE_LABEL,

            // props in AccountDetailsHeader
            // eslint-disable-next-line react/jsx-filename-extension
            headerTitle: <FormattedMessage {...TabMessages.MY_APPLICATIONS} />,
            ctaLabel: TabMessages.START_APPLICATION_CTA,
            startQuoteHREF: getMortgagesNewDocumentHref(locale),

            // props in DocumentHistoryTable
            RowComponent: MortgageApplicationRow,

            // Document
            documentPath: DOCUMENTS.MORTGAGE_JOURNEY.PATH,
            documentType: DOCUMENTS.MORTGAGE_JOURNEY.TYPE,
            schemaVersion: DOCUMENTS.MORTGAGE_JOURNEY.SCHEMA_VERSION,

            // support soft-delete functionality
            metadataFilterFn: metadata => metadata.deleted !== true,
            // process documents after fetching
            extractRows: documents => extractMortgageRows(documents, locale),

            // setting the default tab
            isDefaultTab: true,
        },

        // Auto insurance
        {
            id: TabValues.AUTO,
            tabTitle: TabMessages.AUTO_LABEL,
            headerTitle: <FormattedMessage {...TabMessages.MY_QUOTES} />,
            ctaLabel: TabMessages.START_QUOTE_CTA,
            startQuoteHREF: '/insurance/best-car-insurance-quote',
            RowComponent: SavedDocumentsRowAuto,
            documentPath: DOCUMENTS.INSURANCE_AUTO.PATH,
            documentType: DOCUMENTS.INSURANCE_AUTO.TYPE,
            schemaVersion: DOCUMENTS.INSURANCE_AUTO.SCHEMA_VERSION,
            extractRows: extractAutoHomeRows,
        },

        // Home insurance
        {
            id: TabValues.HOME,
            tabTitle: TabMessages.HOME_LABEL,
            headerTitle: <FormattedMessage {...TabMessages.MY_QUOTES} />,
            ctaLabel: TabMessages.START_QUOTE_CTA,
            startQuoteHREF: '/insurance/best-home-insurance-quote',
            RowComponent: SavedDocumentsRowHome,
            documentPath: DOCUMENTS.INSURANCE_HOME.PATH,
            documentType: DOCUMENTS.INSURANCE_HOME.TYPE,
            schemaVersion: DOCUMENTS.INSURANCE_HOME.SCHEMA_VERSION,
            extractRows: extractAutoHomeRows,
        },

        // Credit cards
        {
            id: TabValues.CREDIT_CARD,
            tabTitle: TabMessages.CREDIT_CARD_LABEL,
            headerTitle: <FormattedMessage {...TabMessages.MY_CREDIT_CARD_SEARCHES} />,
            ctaLabel: TabMessages.START_NEW_SEARCH_CTA,
            startQuoteHREF: getCreditCardURL(locale, CC_URL_SLUGS.CARDFINDER, `?${NEW_DOCUMENT_QUERY_PARAM_KEY}=true`),
            RowComponent: CreditCardApplicationRow,
            documentPath: DOCUMENTS.CREDIT_CARD_JOURNEY.PATH,
            documentType: DOCUMENTS.CREDIT_CARD_JOURNEY.TYPE,
            schemaVersion: DOCUMENTS.CREDIT_CARD_JOURNEY.SCHEMA_VERSION,
            extractRows:  documents => extractCreditCardRows(documents, locale),
        },
    ];
}

export default SavedDocuments;
